import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { ApiService } from '../services/api.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../shared/vte/vte-pagination/vte-pagination.component';

type LogEntry = {
  timestamp: string;
  level: string;
  category: string;
  status_code?: number | null;
  request_id?: string | null;
  user_id?: string | number | null;
  organization_id?: string | number | null;
  ip_address?: string | null;
  method?: string | null;
  endpoint?: string | null;
  duration_ms?: number | null;
  message: string;
  stack_trace?: string | null;
  user_agent?: string | null;
  payload_summary?: any;
  context_data?: any;
};

type DateRangeOption = 'today' | 'yesterday' | 'last_7_days' | 'custom';

@Component({
    selector: 'app-logs',
    imports: [CommonModule, FormsModule, FontAwesomeModule, DatePickerModule, VtePaginationComponent],
    templateUrl: './logs.component.html',
    styleUrls: ['./logs.component.css']
})
export class LogsComponent implements OnInit, OnDestroy {
  readonly levelOptions = ['ALL', 'INFO', 'WARNING', 'ERROR'];
  readonly dateRangeOptions: { label: string; value: DateRangeOption }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last_7_days' },
    { label: 'Custom Date', value: 'custom' }
  ];

  readonly categoryOptions = ['ALL', 'INFO', 'WARNING', 'ERROR', 'SECURITY', 'CLIENT', 'API', 'SYSTEM'];
  readonly statusOptions = [
    { label: 'All Status Codes', value: 'all' },
    { label: '2xx Success', value: '2xx' },
    { label: '4xx Client Errors', value: '4xx' },
    { label: '5xx Server Errors', value: '5xx' },
    { label: '200', value: '200' },
    { label: '201', value: '201' },
    { label: '400', value: '400' },
    { label: '401', value: '401' },
    { label: '403', value: '403' },
    { label: '404', value: '404' },
    { label: '500', value: '500' }
  ];
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;

  logs: LogEntry[] = [];
  loading = false;
  pageSize = 50;
  currentPage = 1;
  totalPages = 1;
  totalRecords = 0;
  dateRange: DateRangeOption = 'today';
  customDate: Date | null = null;
  statusFilter = 'all';
  levelFilter = 'ALL';
  categoryFilter = 'ALL';
  searchTerm = '';
  showDetails = false;
  selectedLog: LogEntry | null = null;

  summaryByLevel: Record<string, number> = {};
  summaryByCategory: Record<string, number> = {};
  summaryByStatus: Record<string, number> = {};

  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(private apiService: ApiService, library: FaIconLibrary) {
    library.addIcons(faAngleLeft, faAngleRight);
  }

  ngOnInit() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.fetchLogs();
    });

    this.fetchLogs();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchLogs() {
    this.loading = true;
    this.apiService.getLogs({
      page: this.currentPage,
      pageSize: this.pageSize,
      dateRange: this.dateRange,
      date: this.dateRange === 'custom' ? this.formatDate(this.customDate) : undefined,
      status: this.statusFilter,
      levels: this.levelFilter === 'ALL' ? [] : [this.levelFilter],
      categories: this.categoryFilter === 'ALL' ? [] : [this.categoryFilter],
      search: this.searchTerm.trim()
    }).subscribe({
      next: (response) => {
        this.logs = response?.lines || [];
        this.totalRecords = response?.count || 0;
        this.totalPages = response?.pagination?.total_pages || 1;
        this.currentPage = response?.pagination?.page || 1;
        this.summaryByLevel = response?.summary?.by_level || {};
        this.summaryByCategory = response?.summary?.by_category || {};
        this.summaryByStatus = response?.summary?.by_status || {};
        this.loading = false;
      },
      error: () => {
        this.logs = [];
        this.totalRecords = 0;
        this.totalPages = 1;
        this.summaryByLevel = {};
        this.summaryByCategory = {};
        this.summaryByStatus = {};
        this.loading = false;
      }
    });
  }

  onDateRangeChange() {
    if (this.dateRange !== 'custom') {
      this.customDate = null;
    }
    this.currentPage = 1;
    this.fetchLogs();
  }

  onCustomDateChange() {
    this.currentPage = 1;
    this.fetchLogs();
  }

  onStatusChange() {
    this.currentPage = 1;
    this.fetchLogs();
  }

  onLevelChange() {
    this.currentPage = 1;
    this.fetchLogs();
  }

  onCategoryChange() {
    this.currentPage = 1;
    this.fetchLogs();
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  clearSearch() {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  next() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.fetchLogs();
    }
  }

  previous() {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.fetchLogs();
    }
  }

  selectPageSize() {
    this.currentPage = 1;
    this.fetchLogs();
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchLogs();
    }

    onPageSizeChange(pageSize: number): void {
        this.pageSize = pageSize;
        this.currentPage = 1;
        this.fetchLogs();
    }

  openDetails(entry: LogEntry) {
    this.selectedLog = entry;
    this.showDetails = true;
  }

  closeDetails() {
    this.showDetails = false;
    this.selectedLog = null;
  }

  formatDate(date: Date | null): string {
    if (!date) {
      return '';
    }
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatValue(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  statusClass(statusCode?: number | null): string {
    if (!statusCode) {
      return 'status-neutral';
    }
    if (statusCode >= 500) {
      return 'status-error';
    }
    if (statusCode >= 400) {
      return 'status-warning';
    }
    if (statusCode >= 200) {
      return 'status-success';
    }
    return 'status-neutral';
  }

  categoryClass(category?: string | null): string {
    return (category || 'system').toLowerCase();
  }
}
